import { deleteUser } from '@/api/services/user';
import { fetchMe } from '@/api/services/user';
import { getSessions } from '@/stores/auth/services';
import { localLogoutUser } from '@/hooks/useAuth';
import { logError } from '@/utils/logHandling';
import { postInfoLogs } from '@/api/services/infoLogs';
import {
  deleteAdditionalContact,
  saveAdditionalContact,
} from '@/api/services/additionalContact';
import { validateEmail } from '@/utils/stringValidator';
import { Animated, Pressable, ScrollView, View } from 'react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/therapist/Button';
import Card from '@/components/ui/therapist/Card';
import { ConfirmDeleteAccount } from '@/components/SharedComponents/CustomDropdown/ConfirmDeleteAccount';
import { Divider } from 'react-native-elements';
import { Header } from '@/components/AccountHeader';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/ui/therapist/Input';
import { PatientStackParamList } from '@/navigations/PatientStack';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import {
  Session,
  SessionsList,
} from '@/components/SharedComponents/SessionsList';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text } from '@/components/Text';
import { TextLink } from '@/components/TextLink';
import { VerifyEmail } from '@/screens/PatientAccount/VerifiyEmail';
import { useDialog } from '@/hooks/useDialog';
import { useNavigation } from '@react-navigation/native';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useRotateAnimation } from '@/hooks/useRotateAnimation';

import colors from '@/config/colors';

import { styles } from './styles';
import { useStoreActions, useStoreState } from '@/stores/store';

export const PatientAccount = () => {
  const [activeSessions, setActiveSessions] = useState<Session[]>([]);
  const [additionalContactEmail, setAdditionalContactEmail] =
    useState<string>('');
  const [verifyAdditionalContactEmail, setVerifyAdditionalContactEmail] =
    useState<string>('');
  const [editEmail, setEditEmail] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { navigate } =
    useNavigation<StackNavigationProp<PatientStackParamList>>();
  const isDeleteEnabledRef = useRef<boolean>(false);
  const setUserData = useStoreActions((actions) => actions.setUserData);
  const { accessToken, user } = useStoreState((state) => state.signedInUser);
  const { showNotification } = useNotification();
  const { showDialog, removeDialog, removeAllDialogs } = useDialog();

  useEffect(() => {
    (async () => {
      const user = await fetchMe();
      setUserData({ accessToken, user });
      user.additionalContact &&
        setAdditionalContactEmail(user.additionalContact.email);
    })();
  }, []);

  if (!user) {
    return null;
  }

  const rotate = useRotateAnimation(isExpanded, 200);

  useEffect(() => {
    (async () => {
      const sessions = await getSessions();
      setActiveSessions(sessions);
    })();
  }, []);

  const onTextChange = (value: string): void => {
    isDeleteEnabledRef.current = false;
    if (value !== 'löschen') {
      return;
    }
    isDeleteEnabledRef.current = true;
  };

  const onSendSupportDataPress = async () => {
    try {
      await postInfoLogs();
      removeAllDialogs();
      showNotification({
        type: 'success',
        title: 'Technische Informationen wurden gesendet',
      });
    } catch (error) {
      logError('Error in onSendSupportDataPress', error);
      showNotification({
        type: 'error',
        title: 'Das Senden der technischen Informationen ist fehlgeschlagen',
      });
    }
  };

  const updateAdditionalContactInfo = async (action: 'update' | 'delete') => {
    const isDelete = action === 'delete';
    try {
      const { user } = isDelete
        ? await deleteAdditionalContact()
        : await saveAdditionalContact({ email: additionalContactEmail });

      setUserData({
        accessToken,
        user,
      });
      showNotification({
        type: 'success',
        title: isDelete
          ? 'Die E-Mail wurde erfolgreich gelöscht.'
          : 'Die Kontaktdaten wurden gespeichert.',
      });
      setEditEmail(false);
      setAdditionalContactEmail('');
      setVerifyAdditionalContactEmail('');
    } catch (error) {
      logError('Error in updateAdditionalContactInfo', error);
      showNotification({
        type: 'error',
        title: isDelete
          ? 'Der Löschvorgang konnte nicht durchgeführt werden.'
          : 'Die Aktualisierung konnte nicht durchgeführt werden.',
      });
    }
  };

  const showConfirmDeleteDialog = () => {
    removeDialog();
    setTimeout(
      () =>
        showDialog({
          title: 'Löschen bestätigen',
          contentComponent: () => (
            <ConfirmDeleteAccount onTextChange={onTextChange} />
          ),
          confirmAction: {
            label: 'Löschen',
            onPress: async () => {
              try {
                if (!isDeleteEnabledRef.current) {
                  return;
                }

                await deleteUser();
                await localLogoutUser(true);
                removeAllDialogs();
              } catch (error) {
                logError('Error in showConfirmDeleteDialog', error);
                showNotification({
                  type: 'error',
                  title:
                    'Das Löschen des Kontos ist fehlgeschlagen. Bitte versuchen Sie es erneut',
                });
              }
            },
          },
          danger: true,
        }),
      100,
    );
  };

  const onDeleteAccountPress = () => {
    removeDialog();
    setTimeout(
      () =>
        showDialog(
          PREPARED_DIALOGS.accountDeleteDialog(showConfirmDeleteDialog),
        ),
      100,
    );
  };

  const onChangePasswordPress = () => {
    removeDialog();
    navigate('PasswordChangeScreen');
  };

  const emailStatus = useMemo(() => {
    if (!additionalContactEmail) {
      return 'default';
    }
    return validateEmail(additionalContactEmail) &&
      additionalContactEmail.toLowerCase() !== user?.email?.toLowerCase()
      ? 'validated'
      : 'warning';
  }, [additionalContactEmail, user?.email]);

  const verifyEmailStatus = useMemo(() => {
    if (!verifyAdditionalContactEmail) {
      return 'default';
    }
    return validateEmail(verifyAdditionalContactEmail) &&
      verifyAdditionalContactEmail.toLowerCase() ===
        additionalContactEmail.toLowerCase()
      ? 'validated'
      : 'warning';
  }, [verifyAdditionalContactEmail, additionalContactEmail]);

  return (
    <ScrollView style={{ width: '100%' }}>
      <View style={styles.accountWrapper}>
        <View style={{ width: '75%' }}>
          <Header user={user} />
          <Card
            title="Anmeldung und Sicherheit"
            icon="lock"
            styleBody={{ padding: 0 }}
          >
            <Divider
              color={colors.lightGrey}
              width={2}
              style={{ marginVertical: 10 }}
            />
            <View style={styles.emailField}>
              <Text style={{ marginRight: 150, width: 80 }}>Email</Text>
              <Text style={{ textAlign: 'left' }}>{user.email}</Text>
            </View>
            <Divider
              color={colors.lightGrey}
              width={2}
              style={{ marginVertical: 10 }}
            />
            <View style={styles.infoWrapper}>
              <View style={{ width: '50%', flexDirection: 'row' }}>
                <Text style={{ marginRight: 150, width: 80 }}>Passwort</Text>
                <Text style={{ textAlign: 'left' }}>{'*'.repeat(16)}</Text>
              </View>
              <Button
                look="tertiary"
                style={{ borderColor: colors.primary, borderWidth: 1 }}
                label="Ändern"
                onPress={onChangePasswordPress}
              />
            </View>
          </Card>
          <Card
            title="Zusätzlicher Kontakt"
            icon="emailOutline"
            styleBody={{ padding: 0 }}
          >
            <Divider
              color={colors.lightGrey}
              width={2}
              style={{ marginVertical: 10 }}
            />
            <View style={{ padding: 20 }}>
              <View style={styles.bannerWrapper}>
                <Icon
                  name="exclamationCircle"
                  style={{ color: colors.primary }}
                  size={30}
                />
                <View style={{ flex: 1, flexDirection: 'column' }}>
                  <Text style={{ fontWeight: '700', paddingBottom: 12 }}>
                    Wozu dient der zusätzliche Kontakt?
                  </Text>
                  <Text style={{ paddingBottom: 8 }}>
                    Sie{' '}
                    {user.additionalContact?.verifiedAt ? 'haben' : 'können'}{' '}
                    die E-Mail-Adresse einer Vertrauensperson (z. B. von
                    Angehörigen) eintragen. Diese Person erhält die gleichen
                    organisatorischen E-Mails wie Sie, z. B. Hinweise zum Ablauf
                    des Rezeptcodes. So kann sie Sie bei Bedarf erinnern oder
                    unterstützen.
                  </Text>
                  <Text>
                    Der eingetragene zusätzliche Kontakt kann Ihren in der App
                    hinterlegten Vor- und Nachnamen sowie Ihre E-Mail-Adresse
                    sehen. Die E-Mail zum Zurücksetzen des Passworts wird nicht
                    an den zusätzlichen Kontakt gesendet.
                  </Text>
                  <Pressable
                    onPress={() => setIsExpanded((p) => !p)}
                    style={styles.showMoreBtn}
                  >
                    <Text style={styles.showMoreText}>Mehr erfahren</Text>
                    <Animated.View style={{ transform: [{ rotate }] }}>
                      <Icon
                        name="chevronDown"
                        size={28}
                        color={colors.primary}
                      />
                    </Animated.View>
                  </Pressable>
                  {isExpanded ? (
                    <View>
                      <Text style={{ fontWeight: '700', paddingBottom: 12 }}>
                        Sie haben Ihre Lizenz auf eigene Rechnung gekauft?
                      </Text>
                      <Text style={{ paddingBottom: 24 }}>
                        Wenn Sie möchten, dass Ihre Vertrauensperson auch
                        E-Mails zu Buchungen, Rechnungen und zum Lizenzablauf
                        vom Zahlungsanbieter erhält, wenden Sie sich bitte an
                        unseren Kundenservice:
                      </Text>
                      <TextLink
                        href="mailto:info@neolexon.de"
                        style={{ paddingBottom: 24 }}
                      >
                        📧 info@neolexon.de
                      </TextLink>
                      <TextLink
                        href="tel:+49089248864440"
                        style={{ paddingBottom: 24 }}
                      >
                        📞 +49 (0)89 24886 4440
                      </TextLink>
                      {!user.additionalContact?.email && (
                        <>
                          <Text
                            style={{ fontWeight: '700', paddingBottom: 12 }}
                          >
                            Was passiert nach dem Speichern?
                          </Text>
                          <Text>
                            Der zusätzliche Kontakt erhält eine
                            Bestätigungs-E-Mail. Erst nach der Bestätigung
                            werden E-Mails an diese Adresse gesendet. Der
                            zusätzliche Kontakt kann jederzeit geändert oder
                            entfernt werden.
                          </Text>
                        </>
                      )}
                    </View>
                  ) : null}
                </View>
              </View>
              {editEmail || !user.additionalContact?.email ? (
                <View style={styles.inputWrapper}>
                  <Text
                    style={{
                      color: colors.newUI.therapist.grey70,
                      marginRight: 'auto',
                    }}
                  >
                    E-Mail-Adresse
                  </Text>
                  <Input
                    value={additionalContactEmail}
                    onChange={setAdditionalContactEmail}
                    placeholder="Zusätzliche E-Mail-Adresse"
                    style={{ width: '100%' }}
                    status={emailStatus}
                    statusMessage={
                      emailStatus === 'warning'
                        ? additionalContactEmail?.toLowerCase() ===
                          user?.email?.toLowerCase()
                          ? 'An Ihre registrierte E-Mail-Adresse werden unsere E-Mails bereits gesendet'
                          : 'Die E-Mail-Adresse ist ungültig'
                        : ''
                    }
                  />
                  <Text
                    style={{
                      color: colors.newUI.therapist.grey70,
                      marginRight: 'auto',
                    }}
                  >
                    E-Mail-Adresse bestätigen
                  </Text>
                  <Input
                    value={verifyAdditionalContactEmail}
                    onChange={setVerifyAdditionalContactEmail}
                    placeholder="Wiederholen Sie die E-Mail-Adresse"
                    style={{ width: '100%' }}
                    status={verifyEmailStatus}
                    statusMessage={
                      verifyEmailStatus === 'warning'
                        ? 'Die E-Mail-Adresse stimmt nicht überein'
                        : ''
                    }
                  />
                  <View style={{ flexDirection: 'row', alignSelf: 'flex-end' }}>
                    {editEmail ? (
                      <Button
                        look="ghost"
                        label="Abbrechen"
                        onPress={() => setEditEmail((prevState) => !prevState)}
                        style={{
                          alignSelf: 'flex-end',
                          marginTop: 4,
                          marginRight: 12,
                        }}
                      />
                    ) : null}
                    <Button
                      look="primary"
                      label="Speichern und verifizieren"
                      onPress={() => updateAdditionalContactInfo('update')}
                      disabled={
                        emailStatus !== 'validated' ||
                        verifyEmailStatus !== 'validated'
                      }
                      style={{ alignSelf: 'flex-end', marginTop: 4 }}
                    />
                  </View>
                </View>
              ) : (
                <VerifyEmail
                  user={user}
                  setEditEmail={() => {
                    setEditEmail((prevState) => !prevState);
                  }}
                  deleteAdditionalContactInfo={() =>
                    updateAdditionalContactInfo('delete')
                  }
                />
              )}
            </View>
          </Card>
          <Card
            title="Aktive Sitzungen"
            icon="television"
            styleBody={{ padding: 0 }}
          >
            <Divider
              color={colors.lightGrey}
              width={2}
              style={{ marginVertical: 10 }}
            />
            <View style={styles.infoWrapper}>
              <SessionsList sessions={activeSessions} />
            </View>
          </Card>
          <Card
            title="Kontoverwaltung"
            icon="cogOutline"
            styleBody={{ padding: 0 }}
          >
            <Divider
              color={colors.lightGrey}
              width={2}
              style={{ marginVertical: 10 }}
            />
            <View style={styles.infoWrapper}>
              <View style={{ width: '70%' }}>
                <Text style={styles.deviceText}>Geräteinformation senden</Text>
                <Text>
                  Wenn ein Fehler in der App auftritt, melden Sie sich bitte bei
                  unserem Support. Wir bitten Sie dann ggf. um Ihre
                  Geräteinformationen gemäß unserer{' '}
                  <TextLink href="https://neolexon.de/datenschutzerklaerung/">
                    Datenschutzerklärung
                  </TextLink>
                  {'. '}
                </Text>
              </View>
              <Button
                look="tertiary"
                style={{ borderColor: colors.primary, borderWidth: 1 }}
                label="Senden"
                icon="send"
                onPress={onSendSupportDataPress}
              />
            </View>
            <Divider
              color={colors.lightGrey}
              width={2}
              style={{ marginVertical: 10 }}
            />
            <View style={styles.infoWrapper}>
              <View style={{ width: '70%' }}>
                <Text style={styles.deleteAccountText}>Konto löschen</Text>
                <Text>
                  Löscht Ihr Benutzerkonto und alle gespeicherten Daten
                  unwiderruflich.
                </Text>
              </View>
              <Button
                look="danger"
                style={{ borderColor: colors.red, borderWidth: 1 }}
                label="Löschen"
                onPress={() => onDeleteAccountPress()}
              />
            </View>
          </Card>
        </View>
      </View>
    </ScrollView>
  );
};
